import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API_URL, getAuthConfig } from "./shared";

/*
 * Laboratoire — Intégration SIL (cahier des charges labo, §27), DEDIE.
 *
 * Port de SILIntegrationModule (HopitalDashboard.js), repointé sur
 * /api/lab/integration/... au lieu de /api/sih/integration/... (voir
 * backend/routes/lab/sil_integration.py, fork de routes/sil_integration.py).
 * Aucune de ces routes ne se termine par un slash final, donc pas de piège
 * de routage comme celui rencontré avec act_types.py.
 */
const SIL_EVENT_LABELS = {
  "patient.created": "Patient créé",
  "patient.updated": "Patient modifié",
  "appointment.created": "Rendez-vous créé",
  "appointment.updated": "Rendez-vous modifié / annulé",
  "reception.arrival": "Arrivée à l'accueil",
  "phlebotomy.received": "Prélèvement reçu au labo",
};
const SIL_STATUS_LABELS = { pending: "En attente", sent: "Envoyé", failed: "Échec" };
const SIL_STATUS_COLORS = {
  pending: ["#FEF3C7", "#854F0B"], sent: ["#DCFCE7", "#166534"], failed: ["#FCEBEB", "#A32D2D"],
};

export default function SILIntegrationModule({ can }) {
  const [config, setConfig] = useState(null);
  const [eventTypes, setEventTypes] = useState([]);
  const [webhookUrl, setWebhookUrl] = useState("");
  const [enabled, setEnabled] = useState(false);
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [newSecret, setNewSecret] = useState(null);
  const [events, setEvents] = useState([]);
  const [eventsTotal, setEventsTotal] = useState(0);
  const [loadingEvents, setLoadingEvents] = useState(true);

  const loadConfig = async () => {
    try {
      const [c, t] = await Promise.all([
        axios.get(`${API_URL}/api/lab/integration/config`, getAuthConfig()),
        axios.get(`${API_URL}/api/lab/integration/event-types`, getAuthConfig()),
      ]);
      setConfig(c.data);
      setWebhookUrl(c.data.webhook_url || "");
      setEnabled(c.data.enabled || false);
      setSelectedEvents(c.data.events_subscribed || []);
      setEventTypes(t.data.event_types || []);
    } catch (e) { setError("Impossible de charger la configuration"); }
  };
  const loadEvents = async () => {
    setLoadingEvents(true);
    try {
      const r = await axios.get(`${API_URL}/api/lab/integration/events`, { ...getAuthConfig(), params: { limit: 50 } });
      setEvents(r.data.events || []); setEventsTotal(r.data.total || 0);
    } catch (e) { /* silencieux : pas de configuration = pas de journal */ }
    finally { setLoadingEvents(false); }
  };
  useEffect(() => { loadConfig(); loadEvents(); }, []);

  const toggleEvent = (id) => {
    setSelectedEvents((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const save = async () => {
    setSaving(true); setError("");
    try {
      const r = await axios.put(`${API_URL}/api/lab/integration/config`, {
        webhook_url: webhookUrl, enabled, events_subscribed: selectedEvents,
      }, getAuthConfig());
      setConfig(r.data);
      toast.success("Configuration enregistrée");
    } catch (e) {
      setError(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally { setSaving(false); }
  };

  const regenerateSecret = async () => {
    try {
      const r = await axios.post(`${API_URL}/api/lab/integration/config/regenerate-secret`, {}, getAuthConfig());
      setNewSecret(r.data.secret);
      loadConfig();
    } catch (e) { toast.error("Erreur lors de la génération du secret"); }
  };

  const retryEvent = async (ev) => {
    try {
      await axios.post(`${API_URL}/api/lab/integration/events/${ev.event_id}/retry`, {}, getAuthConfig());
      toast.success("Nouvel essai envoyé");
      loadEvents();
    } catch (e) { toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur"); }
  };

  const iS = { width: "100%", padding: "9px 12px", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, boxSizing: "border-box", background: "#fff" };
  const lab = { fontSize: 12, color: "#64748B", marginBottom: 4, display: "block" };

  return (
    <div>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: "#0F172A", margin: 0 }}>Intégration SIL</h1>
      <p style={{ fontSize: 13, color: "#94A3B8", marginTop: 4 }}>
        Webhook sortant signé : chaque événement choisi ci-dessous est envoyé en HTTP POST à l'URL configurée, avec une signature HMAC à vérifier côté SIL.
      </p>

      {error && <div style={{ background: "#FCEBEB", border: "1px solid #F7C1C1", color: "#A32D2D", borderRadius: 8, padding: "10px 14px", marginTop: 14, fontSize: 13 }}>{error}</div>}

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, padding: 20 }}>
        <div style={{ fontSize: 15, fontWeight: 600, color: "#0F172A", marginBottom: 14 }}>Configuration</div>

        <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", marginBottom: 14, cursor: "pointer" }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} disabled={!can("integration:write")} />
          Intégration active
        </label>

        <label style={lab}>URL du webhook</label>
        <input style={iS} value={webhookUrl} onChange={(e) => setWebhookUrl(e.target.value)}
               placeholder="https://votre-sil.exemple.dz/webhooks/tabbibak" disabled={!can("integration:write")} />

        <div style={{ marginTop: 16 }}>
          <label style={lab}>Événements à transmettre</label>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {eventTypes.map((et) => (
              <label key={et} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer" }}>
                <input type="checkbox" checked={selectedEvents.includes(et)} onChange={() => toggleEvent(et)} disabled={!can("integration:write")} />
                {SIL_EVENT_LABELS[et] || et}
              </label>
            ))}
          </div>
        </div>

        {can("integration:write") && (
          <div style={{ display: "flex", gap: 10, marginTop: 18 }}>
            <button onClick={save} disabled={saving} style={{ padding: "9px 18px", background: "#185FA5", color: "#fff", border: "none", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              {saving ? "Enregistrement..." : "Enregistrer"}
            </button>
            <button onClick={regenerateSecret} style={{ padding: "9px 18px", background: "#fff", color: "#334155", border: "1px solid #CBD5E1", borderRadius: 8, fontSize: 14, cursor: "pointer" }}>
              {config?.secret_configured ? "Générer un nouveau secret" : "Générer un secret"}
            </button>
          </div>
        )}

        {config?.secret_configured && !newSecret && (
          <div style={{ fontSize: 12, color: "#94A3B8", marginTop: 10 }}>Secret configuré (se termine par « {config.secret_hint} »).</div>
        )}

        {newSecret && (
          <div style={{ background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: 8, padding: "12px 14px", marginTop: 14, fontSize: 13 }}>
            <div style={{ fontWeight: 600, color: "#854F0B", marginBottom: 6 }}>Notez ce secret maintenant — il ne sera plus jamais affiché :</div>
            <code style={{ fontSize: 13, color: "#0F172A", wordBreak: "break-all" }}>{newSecret}</code>
            <div style={{ marginTop: 8 }}>
              <button onClick={() => setNewSecret(null)} style={{ fontSize: 12, color: "#185FA5", background: "none", border: "none", cursor: "pointer" }}>J'ai noté le secret</button>
            </div>
          </div>
        )}
      </div>

      <div style={{ background: "#fff", border: "1px solid #E2E8F0", borderRadius: 12, marginTop: 16, overflow: "hidden" }}>
        <div style={{ padding: "14px 16px", borderBottom: "1px solid #F1F5F9", fontSize: 15, fontWeight: 600, color: "#0F172A" }}>
          Journal des envois {eventsTotal ? `(${eventsTotal})` : ""}
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr style={{ background: "#F8FAFC", textAlign: "left" }}>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Date</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Événement</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Statut</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Réponse</th>
            <th style={{ padding: "10px 14px", fontSize: 12, color: "#64748B", fontWeight: 600 }}>Actions</th>
          </tr></thead>
          <tbody>
            {loadingEvents && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Chargement...</td></tr>}
            {!loadingEvents && events.length === 0 && <tr><td colSpan={5} style={{ padding: "16px 14px", fontSize: 13, color: "#94A3B8", textAlign: "center" }}>Aucun événement envoyé.</td></tr>}
            {!loadingEvents && events.map((ev) => { const sc = SIL_STATUS_COLORS[ev.status] || ["#F1F5F9", "#64748B"]; return (
              <tr key={ev.event_id} style={{ borderTop: "1px solid #F1F5F9" }}>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#475569" }}>{new Date(ev.created_at).toLocaleString("fr-FR")}</td>
                <td style={{ padding: "12px 14px", fontSize: 13, color: "#0F172A" }}>{SIL_EVENT_LABELS[ev.event_type] || ev.event_type}</td>
                <td style={{ padding: "12px 14px" }}><span style={{ fontSize: 12, padding: "3px 10px", borderRadius: 12, background: sc[0], color: sc[1] }}>{SIL_STATUS_LABELS[ev.status] || ev.status}</span></td>
                <td style={{ padding: "12px 14px", fontSize: 12, color: "#94A3B8" }}>{ev.response_code || ev.last_error || "—"}</td>
                <td style={{ padding: "12px 14px" }}>
                  {ev.status === "failed" && can("integration:write") && (
                    <button onClick={() => retryEvent(ev)} style={{ fontSize: 13, color: "#185FA5", background: "none", border: "none", cursor: "pointer", fontWeight: 600 }}>Réessayer</button>
                  )}
                </td>
              </tr>); })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
